import {
  ConfiguredFlow,
  DataSettingsColumnId,
  RelatedRulesChipPalette,
} from 'app/modules/dataSettings/types';
import {
  ActionEventNativeFields,
  AddressNativeFields,
  AllNativeFields,
  CensorshipSetting,
  DataSettingFieldType,
  EntityNativeFields,
  InstrumentNativeFields,
  NativeDataSettingsConfigResponse,
  RenderingOptionsResponse,
  SemanticTypes,
  TxnEventNativeFields,
  Unit21DataClassifier,
} from 'app/modules/dataSettings/responses';
import {
  U21SelectOptionProps,
  U21TableColumnTypes,
} from 'app/shared/u21-ui/components';
import {
  PrimaryObject,
  SecondaryObject,
  U21Object,
} from 'app/modules/dataMapping/types';
import { RuleStatuses } from 'app/modules/rules/models';

export const HUMAN_READABLE_CUSTOM_DATA_FIELD_TYPE: Record<
  DataSettingFieldType,
  string
> = {
  [DataSettingFieldType.DATE]: 'Date',
  [DataSettingFieldType.DATE_TIME]: 'Datetime',
  [DataSettingFieldType.NUMBER]: 'Number',
  [DataSettingFieldType.TEXT]: 'Text',
  [DataSettingFieldType.LIST]: 'List',
  [DataSettingFieldType.ENUM]: 'Enum',
  [DataSettingFieldType.BOOLEAN]: 'Boolean',
  [DataSettingFieldType.ENTITY_REFERENCE]: 'Entity reference',
  [DataSettingFieldType.INSTRUMENT_REFERENCE]: 'Instrument reference',
  [DataSettingFieldType.JSON]: 'JSON',
  [DataSettingFieldType.ANY]: 'Any',
};

export const HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE: Record<
  Unit21DataClassifier,
  string
> = {
  [Unit21DataClassifier.ENTITY]: 'Entity',
  [Unit21DataClassifier.EVENT]: 'Transaction',
  [Unit21DataClassifier.ACTION_EVENT]: 'Action',
  [Unit21DataClassifier.INSTRUMENT]: 'Instrument',
  [Unit21DataClassifier.RULE]: 'Rule',
  [Unit21DataClassifier.ALERT]: 'Alert',
  [Unit21DataClassifier.CASE]: 'Case',
  [Unit21DataClassifier.ADDRESS]: 'Address',
  [Unit21DataClassifier.WATCHLIST]: 'Watchlist',
  [Unit21DataClassifier.CLIENT_FINGERPRINT]: 'Client fingerprint',
  [Unit21DataClassifier.EMAIL_ADDRESS]: 'Email address',
  [Unit21DataClassifier.IP_ADDRESS]: 'IP address',
  [Unit21DataClassifier.PHONE_NUMBER]: 'Phone number',
  [Unit21DataClassifier.PHYSICAL_ID]: 'Physical ID',
};

export const HUMAN_READABLE_SEMANTIC_TYPE: Record<SemanticTypes, string> = {
  [SemanticTypes.ACCOUNT_NUMBER]: 'Account number',
  [SemanticTypes.ACH_SEC_CODE]: 'ACH SEC code',
  [SemanticTypes.ACH_RETURN_CODE]: 'ACH return code',
  [SemanticTypes.CHECK_MEMO]: 'Check memo',
  [SemanticTypes.CHECK_NUMBER]: 'Check number',
  [SemanticTypes.EMAIL_ADDRESS]: 'Email address',
  [SemanticTypes.INSTITUTION_NUMBER]: 'Institution number',
  [SemanticTypes.JH_CHECK_IMAGE_NUMBER]: 'Jack Henry check image number',
  [SemanticTypes.MCC_CODE]: 'Merchant Category Code (MCCs)',
  [SemanticTypes.NACHA_DESCRIPTION]: 'NACHA merchant description',
  [SemanticTypes.NAICS_CODE]: 'NAICS code',
  [SemanticTypes.ROUTING_NUMBER]: 'Routing number',
  [SemanticTypes.TRACE_ID]: 'Trace ID',
  [SemanticTypes.URL]: 'URL',
};

export const SEMANTIC_TYPE_DESCRIPTION: Partial<Record<SemanticTypes, string>> =
  {
    [SemanticTypes.ACCOUNT_NUMBER]: 'MICR bank account number (American)',
    [SemanticTypes.ACH_SEC_CODE]:
      'Three letter code that describes how a payment was authorized by the consumer or business receiving an ACH transaction. SEC stands for `Standard Entry Class`. SEC codes are defined and maintained by NACHA, the governing body for the ACH network.',
    [SemanticTypes.ACH_RETURN_CODE]:
      "ACH return codes identify the reason an ACH payment was returned by the recipient's bank. They make it easier for originating and receiving financial institutions to spot and communicate payment failures.",
    [SemanticTypes.CHECK_MEMO]: 'Memo line on checks',
    [SemanticTypes.INSTITUTION_NUMBER]:
      'Three-digit number that refers to a specific bank. Present on many check data sets',
    [SemanticTypes.NACHA_DESCRIPTION]:
      'The NACHA description gives a short description of the merchant on ACH & direct deposit payments. i.e `TARGET 0000XXX, Townsville, OR 971XX US`',
    [SemanticTypes.NAICS_CODE]:
      'North American Industry Classification System, classifying business establishments',
    [SemanticTypes.ROUTING_NUMBER]:
      'MICR bank account routing number (American)',
    [SemanticTypes.TRACE_ID]:
      'Trace referrence common for multi-stage transaction ledgers',
  };

export const DATA_CLASSIFIER_SELECT_OPTIONS = [
  {
    text: 'Entity',
    value: Unit21DataClassifier.ENTITY,
  },
  {
    text: 'Transaction',
    value: Unit21DataClassifier.EVENT,
  },
  {
    text: 'Action event',
    value: Unit21DataClassifier.ACTION_EVENT,
  },
  {
    text: 'Instrument',
    value: Unit21DataClassifier.INSTRUMENT,
  },
  {
    text: 'Alert',
    value: Unit21DataClassifier.ALERT,
  },
  {
    text: 'Address',
    value: Unit21DataClassifier.ADDRESS,
  },
  {
    text: 'Watchlist',
    value: Unit21DataClassifier.WATCHLIST,
  },
];

export const SEMANTIC_TYPE_OPTIONS: U21SelectOptionProps[] = Object.values(
  SemanticTypes,
).map((value) => ({
  value,
  text: HUMAN_READABLE_SEMANTIC_TYPE[value],
  description: SEMANTIC_TYPE_DESCRIPTION[value],
}));

// plz alphabetize
export const SEMANTIC_TYPES_TO_U21_DATA_CLASSIFIER: Partial<
  Record<Unit21DataClassifier, Set<SemanticTypes>>
> = {
  [Unit21DataClassifier.ENTITY]: new Set([
    SemanticTypes.EMAIL_ADDRESS,
    SemanticTypes.NAICS_CODE,
    SemanticTypes.URL,
  ]),
  [Unit21DataClassifier.INSTRUMENT]: new Set([
    SemanticTypes.ACCOUNT_NUMBER,
    SemanticTypes.ROUTING_NUMBER,
  ]),
  [Unit21DataClassifier.EVENT]: new Set([
    SemanticTypes.ACCOUNT_NUMBER,
    SemanticTypes.ACH_RETURN_CODE,
    SemanticTypes.ACH_SEC_CODE,
    SemanticTypes.CHECK_MEMO,
    SemanticTypes.CHECK_NUMBER,
    SemanticTypes.INSTITUTION_NUMBER,
    SemanticTypes.JH_CHECK_IMAGE_NUMBER,
    SemanticTypes.MCC_CODE,
    SemanticTypes.NACHA_DESCRIPTION,
    SemanticTypes.ROUTING_NUMBER,
    SemanticTypes.TRACE_ID,
  ]),
};

export const RELATED_RULES_CHIP_COLORS: RelatedRulesChipPalette = {
  ACTIVE: {
    color: 'success',
    style: 'filled',
  },
  VALIDATION: {
    color: 'warning',
    style: 'ghost',
  },
  ACTIVE_SHADOW: {
    color: 'success',
    style: 'ghost',
  },
  PENDING_ACTIVATION: {
    color: 'success',
    style: 'filled',
  },
  PENDING_SHADOW_ACTIVATION: {
    color: 'success',
    style: 'ghost',
  },
  PUBLISHED: {
    color: 'success',
    style: 'filled',
  },
  ARCHIVED: {
    color: 'default',
    style: 'ghost',
  },
  EXPIRED: {
    color: 'default',
    style: 'ghost',
  },
  SHADOW_MIGRATION: {
    color: 'default',
    style: 'ghost',
  },
};

export const DATA_SETTING_KEY_TYPE_READABLE_NAME: Record<string, string> = {
  NATIVE: 'Unit21 Field',
  CUSTOM: 'Custom Data Field',
};

export const DATA_SETTINGS_FIELD_TYPE_OPTIONS: U21SelectOptionProps[] =
  Object.values(DataSettingFieldType).map((val) => ({
    text: HUMAN_READABLE_CUSTOM_DATA_FIELD_TYPE[val],
    value: val,
  }));

export const DATA_SETTINGS_COLUMNS_KEY_LABEL_MAP = {
  [DataSettingsColumnId.DEFAULT_NAME]: 'Default name',
  [DataSettingsColumnId.FRIENDLY_NAME]: 'Friendly name',
  [DataSettingsColumnId.FIELD_TYPE]: 'Field type',
  [DataSettingsColumnId.DATA_TYPE]: 'Data type',
  [DataSettingsColumnId.OBJECT_TYPE]: 'Object',
  [DataSettingsColumnId.EXPORTED]: 'Exported',
  [DataSettingsColumnId.CENSORSHIP_SETTING]: 'Censorship setting',
  [DataSettingsColumnId.UPDATED_AT]: 'Last ingested date',
  [DataSettingsColumnId.USE_IN_GRAPH_BASED_RULES]: 'Use in graph based rules',
  [DataSettingsColumnId.SEMANTIC_TYPE]: 'Semantic type',
  [DataSettingsColumnId.DESCRIPTION]: 'Description',
  [DataSettingsColumnId.RELATED_RULES]: 'Related rules',
  [DataSettingsColumnId.RECENT_VALUES]: 'Recent values',
  [DataSettingsColumnId.ENUM_LIST]: 'Enum set',
} as const;

const DEFAULT_HIDDEN_COLS = new Set([
  DataSettingsColumnId.DESCRIPTION,
  DataSettingsColumnId.RELATED_RULES,
  DataSettingsColumnId.RECENT_VALUES,
  DataSettingsColumnId.ENUM_LIST,
  DataSettingsColumnId.USE_IN_GRAPH_BASED_RULES,
]);

export const DEFAULT_DATA_SETTINGS_TABLE_CONFIG = Object.values(
  DataSettingsColumnId,
).reduce<{ key: DataSettingsColumnId; label: string; hidden: boolean }[]>(
  (acc, key) => {
    acc.push({
      key,
      label: DATA_SETTINGS_COLUMNS_KEY_LABEL_MAP[key],
      hidden: DEFAULT_HIDDEN_COLS.has(key),
    });
    return acc;
  },
  [],
);

export const UNIT21_DATA_CLASSIFIER_TO_U21_OBJECT_TYPE: Record<
  Unit21DataClassifier,
  U21Object | undefined
> = {
  [Unit21DataClassifier.ACTION_EVENT]: PrimaryObject.ACTION,
  [Unit21DataClassifier.EVENT]: PrimaryObject.TRANSACTION,
  [Unit21DataClassifier.ENTITY]: PrimaryObject.ENTITY,
  [Unit21DataClassifier.INSTRUMENT]: PrimaryObject.INSTRUMENT,
  [Unit21DataClassifier.ALERT]: PrimaryObject.ALERT,
  [Unit21DataClassifier.ADDRESS]: SecondaryObject.ADDRESS,
  [Unit21DataClassifier.CLIENT_FINGERPRINT]: SecondaryObject.CLIENT_FINGERPRINT,
  [Unit21DataClassifier.EMAIL_ADDRESS]: SecondaryObject.EMAIL_ADDRESS,
  [Unit21DataClassifier.IP_ADDRESS]: SecondaryObject.IP_ADDRESS,
  [Unit21DataClassifier.PHONE_NUMBER]: SecondaryObject.PHONE_NUMBER,
  [Unit21DataClassifier.PHYSICAL_ID]: SecondaryObject.DOCUMENT,
  [Unit21DataClassifier.CASE]: undefined,
  [Unit21DataClassifier.RULE]: undefined,
  [Unit21DataClassifier.WATCHLIST]: undefined,
};

const ACTION_EVENT_MISMATCHED_KEYS: Partial<
  Record<ActionEventNativeFields, string>
> = {
  [ActionEventNativeFields.EVENT_ID]: 'external_id',
};

const TXN_EVENT_MISMATCHED_KEYS: Partial<Record<TxnEventNativeFields, string>> =
  {
    [TxnEventNativeFields.CONVERSION_NOTES]: 'usd_conversion_notes',
    [TxnEventNativeFields.EVENT_ID]: 'external_id',
    [TxnEventNativeFields.FEE]: 'internal_fee',
    [TxnEventNativeFields.TYPE]: 'internal_txn_type',
    [TxnEventNativeFields.IP_ADDRESS]: 'ip_address.ip_address',
  };

const ENTITY_MISMATCHED_KEYS: Partial<Record<EntityNativeFields, string>> = {
  [EntityNativeFields.BUSINESS_NAME]: 'name',
  [EntityNativeFields.ENTITY_TYPE]: 'type',
  [EntityNativeFields.ENTITY_SUBTYPE]: 'internal_entity_type',
};

const INSTRUMENT_MISMATCHED_KEYS: Partial<
  Record<InstrumentNativeFields, string>
> = {
  [InstrumentNativeFields.INSTRUMENT_ID]: 'external_id',
};

const ADDRESS_MISMATCHED_KEYS: Partial<Record<AddressNativeFields, string>> = {
  [AddressNativeFields.POSTAL_CODE]: 'zip_code',
};

// certain native data settings' native_keys do not match the keys used in our schema, so this map rectifies that.
// to use it when doing data-settings-based lookups, do something like:
// response[DATA_SETTINGS_ENUM_TO_NATIVE_KEY[ds.unit21_data_classifier][ds.native_key] ?? ds.native_key]
export const NATIVE_DATA_SETTINGS_NATIVE_KEY_MISMATCH_MAP: Record<
  Unit21DataClassifier,
  Partial<Record<AllNativeFields, string>>
> = {
  [Unit21DataClassifier.ACTION_EVENT]: ACTION_EVENT_MISMATCHED_KEYS,
  [Unit21DataClassifier.EVENT]: TXN_EVENT_MISMATCHED_KEYS,
  [Unit21DataClassifier.ENTITY]: ENTITY_MISMATCHED_KEYS,
  [Unit21DataClassifier.INSTRUMENT]: INSTRUMENT_MISMATCHED_KEYS,
  [Unit21DataClassifier.ALERT]: {},
  [Unit21DataClassifier.ADDRESS]: ADDRESS_MISMATCHED_KEYS,
  [Unit21DataClassifier.CASE]: {},
  [Unit21DataClassifier.RULE]: {},
  [Unit21DataClassifier.WATCHLIST]: {},
  [Unit21DataClassifier.CLIENT_FINGERPRINT]: {},
  [Unit21DataClassifier.EMAIL_ADDRESS]: {},
  [Unit21DataClassifier.IP_ADDRESS]: {},
  [Unit21DataClassifier.PHONE_NUMBER]: {},
  [Unit21DataClassifier.PHYSICAL_ID]: {},
};

export const DATA_SETTING_FIELD_TYPE_TO_COLUMN_TYPE: Partial<
  Record<DataSettingFieldType, U21TableColumnTypes>
> = {
  [DataSettingFieldType.BOOLEAN]: U21TableColumnTypes.BOOLEAN,
  [DataSettingFieldType.DATE]: U21TableColumnTypes.DATE,
  [DataSettingFieldType.DATE_TIME]: U21TableColumnTypes.DATETIME,
};

export const NUMBER_PRECISION_RENDERING_OPTIONS: U21SelectOptionProps[] = [
  {
    text: 'Raw value',
    value: null,
  },
  ...Array.from({ length: 9 }, (_, i) => ({
    text: i.toString(),
    value: i,
  })),
];

export const FLOW_TO_READABLE_DIRECTIONALITY: Record<ConfiguredFlow, string> = {
  INBOUND: 'Receiving',
  OUTBOUND: 'Sending',
  OTHER: 'Other',
};

export const CONFIGURED_FLOW_OPTIONS: U21SelectOptionProps<ConfiguredFlow>[] = [
  {
    value: 'OUTBOUND' as const,
    text: FLOW_TO_READABLE_DIRECTIONALITY.OUTBOUND,
  },
  {
    value: 'INBOUND' as const,
    text: FLOW_TO_READABLE_DIRECTIONALITY.INBOUND,
  },
  { value: 'OTHER' as const, text: FLOW_TO_READABLE_DIRECTIONALITY.OTHER },
];

export enum ReferenceDisplayField {
  NAME_READABLE = 'name_readable',
  EXTERNAL_ID = 'external_id',
}

export const DEFAULT_REFERENCE_DISPLAY_FIELDS = [
  ReferenceDisplayField.NAME_READABLE,
];

export const REFERENCE_DISPLAY_FIELDS_OPTIONS: U21SelectOptionProps[] = [
  { value: ReferenceDisplayField.EXTERNAL_ID, text: 'Entity ID' },
  { value: ReferenceDisplayField.NAME_READABLE, text: 'Name' },
];

export const READABLE_CENSORSHIP_SETTING: Record<CensorshipSetting, string> = {
  ALWAYS_VISIBLE: 'Always visible',
  CENSORED_BUT_VIEWABLE: 'Censored but viewable',
  CENSORED: 'Censored',
  ENCRYPTED: 'Encrypted',
};

export const CENSORSHIP_SETTING_OPTIONS: U21SelectOptionProps[] = [
  {
    value: CensorshipSetting.ALWAYS_VISIBLE,
    text: READABLE_CENSORSHIP_SETTING[CensorshipSetting.ALWAYS_VISIBLE],
    description: 'The value will always be rendered for all agents',
  },
  {
    value: CensorshipSetting.CENSORED_BUT_VIEWABLE,
    text: READABLE_CENSORSHIP_SETTING[CensorshipSetting.CENSORED_BUT_VIEWABLE],
    description:
      'The value will appear obscured, but will be visible with a click',
  },
  {
    value: CensorshipSetting.CENSORED,
    text: READABLE_CENSORSHIP_SETTING[CensorshipSetting.CENSORED],
    description:
      'The value will appear obscured, and will not be visible with a click unless permission is explicitly granted',
  },
  {
    value: CensorshipSetting.ENCRYPTED,
    text: 'Encrypted',
    description:
      'The value is encrypted and is decryptable with a click so long as permission to decrypt encrypted data is provided.',
  },
];

export const NON_CENSORABLE_NATIVE_DATA_SETTINGS: Record<
  Unit21DataClassifier,
  AllNativeFields[]
> = {
  // these fields are unavailable to be configured with censorship setting because the values are used in some way that we can't hide
  ENTITY: [
    EntityNativeFields.FIRST_NAME,
    EntityNativeFields.MIDDLE_NAME,
    EntityNativeFields.LAST_NAME,
    EntityNativeFields.BUSINESS_NAME,
    EntityNativeFields.USER_NAME,
    EntityNativeFields.ENTITY_TYPE,
    EntityNativeFields.EXTERNAL_ID,
  ],
  INSTRUMENT: [
    InstrumentNativeFields.INSTRUMENT_TYPE,
    InstrumentNativeFields.INSTRUMENT_ID,
  ],
  ACTION_EVENT: [
    ActionEventNativeFields.EVENT_ID,
    ActionEventNativeFields.ACTION_TYPE,
    ActionEventNativeFields.EVENT_TIME,
  ],
  EVENT: [
    TxnEventNativeFields.EVENT_ID,
    TxnEventNativeFields.EVENT_TIME,
    TxnEventNativeFields.TYPE,
    TxnEventNativeFields.AMOUNT,
  ],
  ALERT: [],
  CASE: [],
  RULE: [],
  ADDRESS: [],
  WATCHLIST: [],
  CLIENT_FINGERPRINT: [],
  EMAIL_ADDRESS: [],
  IP_ADDRESS: [],
  PHONE_NUMBER: [],
  PHYSICAL_ID: [],
};

export const BASE_DATA_SETTINGS_BY_NATIVE_KEY = Object.values(
  Unit21DataClassifier,
).reduce(
  (acc, classifier) => ({
    ...acc,
    [classifier]: {},
  }),
  {} as Record<
    Unit21DataClassifier,
    Record<string, NativeDataSettingsConfigResponse>
  >,
);

export const MAX_ALLOWED_ENUMS = 250;

export const RENDERING_OPTIONS_BY_DATA_TYPE: Record<
  DataSettingFieldType,
  (keyof RenderingOptionsResponse)[]
> = {
  [DataSettingFieldType.DATE]: [],
  [DataSettingFieldType.DATE_TIME]: ['timezone'],
  [DataSettingFieldType.NUMBER]: [
    'precision',
    'is_url',
    'url_prefix',
    'url_postfix',
  ],
  [DataSettingFieldType.TEXT]: ['is_url', 'url_prefix', 'url_postfix'],
  [DataSettingFieldType.LIST]: [],
  [DataSettingFieldType.ENUM]: ['enum_mapping'],
  [DataSettingFieldType.BOOLEAN]: [],
  [DataSettingFieldType.ENTITY_REFERENCE]: ['reference_display_fields'],
  [DataSettingFieldType.INSTRUMENT_REFERENCE]: [],
  [DataSettingFieldType.JSON]: [],
  [DataSettingFieldType.ANY]: [],
};

export const RULES_ORDER: RuleStatuses[] = [
  RuleStatuses.ACTIVE,
  RuleStatuses.ACTIVE_SHADOW,
  RuleStatuses.VALIDATION,
  RuleStatuses.ARCHIVED,
  RuleStatuses.EXPIRED,
];

export const REFERENCE_TYPES = new Set([
  DataSettingFieldType.ENTITY_REFERENCE,
  DataSettingFieldType.INSTRUMENT_REFERENCE,
]);
export const DATA_TYPES_WITH_RENDERING_OPTIONS = new Set(
  Object.entries(RENDERING_OPTIONS_BY_DATA_TYPE)
    .filter((entries) => entries[1].length)
    .map((entries) => entries[0] as DataSettingFieldType),
);

export const DEVICE_INTELLIGENCE_FIELD_ORDER: TxnEventNativeFields[] = [
  // Identity/Visitor Fields
  TxnEventNativeFields.FINGERPRINT_VISITOR_ID,
  TxnEventNativeFields.FINGERPRINT_FIRST_SEEN,
  TxnEventNativeFields.FINGERPRINT_LAST_SEEN,
  TxnEventNativeFields.FINGERPRINT_CONFIDENCE_SCORE,
  // Boolean Detection Fields
  TxnEventNativeFields.FINGERPRINT_ANDROID_EMULATOR,
  TxnEventNativeFields.FINGERPRINT_BROWSER_TAMPERING,
  TxnEventNativeFields.FINGERPRINT_CLONED_APP,
  TxnEventNativeFields.FINGERPRINT_DEVELOPER_TOOLS,
  TxnEventNativeFields.FINGERPRINT_FRIDA_DETECTED,
  TxnEventNativeFields.FINGERPRINT_INCOGNITO_MODE,
  TxnEventNativeFields.FINGERPRINT_JAILBROKEN,
  TxnEventNativeFields.FINGERPRINT_LOCATION_SPOOFING,
  TxnEventNativeFields.FINGERPRINT_MALICIOUS_BOT,
  TxnEventNativeFields.FINGERPRINT_MITM_ATTACK,
  TxnEventNativeFields.FINGERPRINT_NEWLY_SEEN,
  TxnEventNativeFields.FINGERPRINT_PRIVACY_SETTINGS,
  TxnEventNativeFields.FINGERPRINT_REMOTE_CONTROL,
  TxnEventNativeFields.FINGERPRINT_ROOTED_DEVICE,
  TxnEventNativeFields.FINGERPRINT_TIMEZONE_MISMATCH,
  TxnEventNativeFields.FINGERPRINT_VIRTUAL_MACHINE,
  // IP-Related Fields (Grouped Together - Prioritized)
  TxnEventNativeFields.FINGERPRINT_IP_ADDRESS,
  TxnEventNativeFields.FINGERPRINT_IP_LATITUDE,
  TxnEventNativeFields.FINGERPRINT_IP_LONGITUDE,
  TxnEventNativeFields.FINGERPRINT_IP_CITY,
  TxnEventNativeFields.FINGERPRINT_IP_COUNTRY,
  TxnEventNativeFields.FINGERPRINT_IP_CONTINENT,
  TxnEventNativeFields.FINGERPRINT_IP_TIMEZONE,
  TxnEventNativeFields.FINGERPRINT_IP_ASN,
  TxnEventNativeFields.FINGERPRINT_IP_DATACENTER,
  TxnEventNativeFields.FINGERPRINT_IP_BLOCKLIST,
  TxnEventNativeFields.FINGERPRINT_IP_PROXY,
  TxnEventNativeFields.FINGERPRINT_IP_TOR,
  TxnEventNativeFields.FINGERPRINT_IP_VPN,
  // Device/Browser Information Fields
  TxnEventNativeFields.FINGERPRINT_DEVICE_NAME,
  TxnEventNativeFields.FINGERPRINT_OS_NAME,
  TxnEventNativeFields.FINGERPRINT_OS_VERSION,
  TxnEventNativeFields.FINGERPRINT_BROWSER_NAME,
  TxnEventNativeFields.FINGERPRINT_BROWSER_VERSION,
  // Activity/Metrics Fields
  TxnEventNativeFields.FINGERPRINT_DAILY_REQUESTS,
  TxnEventNativeFields.FINGERPRINT_HIGH_ACTIVITY_THRESHOLD,
  TxnEventNativeFields.FINGERPRINT_FACTORY_RESET_TIME,
  // Suspect Score Fields
  TxnEventNativeFields.FINGERPRINT_SUSPECT_SCORE,
  // Bot Information Fields
  TxnEventNativeFields.FINGERPRINT_BOT,
  TxnEventNativeFields.FINGERPRINT_BOT_TYPE,
];

/*
  This is a list of data settings that are not available in the standard UI,
  for example on tables and in sidebars/details pages.
  However, we still have data settings for them because the fields should be available in the DMB.
*/
export const UI_UNAVAILABLE_DATA_SETTINGS_BY_CLASSIFIER: Partial<
  Record<Unit21DataClassifier, Set<AllNativeFields>>
> = {
  [Unit21DataClassifier.ENTITY]: new Set([
    EntityNativeFields.U21_LATEST_RISK_LEVEL,
    EntityNativeFields.U21_LATEST_RISK_SCORE,
    EntityNativeFields.U21_LATEST_RISK_SEGMENT,
  ]),
  [Unit21DataClassifier.EVENT]: new Set([
    ...Object.values(TxnEventNativeFields).filter(
      (val) =>
        val.startsWith('fingerprint_') ||
        val.startsWith('focsec_') ||
        val.startsWith('geolocation_'),
    ),
  ]),
  [Unit21DataClassifier.ACTION_EVENT]: new Set([
    ...Object.values(ActionEventNativeFields).filter(
      (val) =>
        val.startsWith('fingerprint_') ||
        val.startsWith('focsec_') ||
        val.startsWith('geolocation_'),
    ),
  ]),
};
